import { useState, type FormEvent } from 'react'
import type { useAuth } from './useAuth'

/**
 * The real Atlas registration/login gate in front of a fake catalog. Shown in
 * place of the composer until a real session exists — see
 * netlify/functions/shelve.ts for why the submission endpoint enforces this
 * server-side too, rather than trusting this panel to have run.
 */
export default function AuthPanel({ auth }: { auth: ReturnType<typeof useAuth> }) {
  const [tab, setTab] = useState<'login' | 'register'>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (tab === 'login') void auth.login(email, password)
    else void auth.register(email, password, name)
  }

  return (
    <form className="composer auth" onSubmit={onSubmit}>
      <div className="tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'login'}
          className={tab === 'login' ? 'active' : ''}
          onClick={() => { setTab('login'); auth.dismissError() }}
        >
          Log in
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'register'}
          className={tab === 'register' ? 'active' : ''}
          onClick={() => { setTab('register'); auth.dismissError() }}
        >
          Create an account
        </button>
      </div>

      <p className="hint">
        A real GameCodex account is required to submit a game — the catalog is fiction, the account behind it
        isn't.
      </p>

      {tab === 'register' && (
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name (optional)"
          aria-label="Name"
        />
      )}
      <input
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        type="email"
        placeholder="Email"
        aria-label="Email"
        required
      />
      <input
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        type="password"
        placeholder="Password"
        aria-label="Password"
        minLength={8}
        required
      />

      <div className="row">
        <button type="submit" disabled={auth.pending || !email.trim() || password.length < 8}>
          {auth.pending ? 'Please wait…' : tab === 'login' ? 'Log in' : 'Create account'}
        </button>
      </div>

      {auth.error && (
        <p className="err" role="alert">
          {auth.error}
        </p>
      )}
    </form>
  )
}
